import type { ReactNode } from "react";
import { CopyableValue } from "./CopyableValue";
import type { CopyOption } from "./CopyableValue";

export interface KeyValueItem {
  label: string;
  value?: ReactNode;
  /** Plain text for clipboard copy */
  copyValue?: string | undefined;
  /** Multiple copy options shown in the hover popover */
  copyOptions?: CopyOption[] | undefined;
  /**
   * Set false to render the value as-is with no copy affordance — use when the
   * value is itself interactive (e.g. a link) so it isn't wrapped in a
   * click-to-copy span. Defaults to copyable.
   */
  copyable?: boolean | undefined;
  /**
   * Nested key/values rendered as an indented group beneath this row. When set,
   * this row becomes a group header (its own `value` is not shown) and the
   * sub-items render under a left accent rule as a connected block.
   */
  subItems?: KeyValueItem[] | undefined;
}

interface KeyValueListProps {
  items: KeyValueItem[];
}

function ValueCell_({ item }: { item: KeyValueItem }) {
  return (
    // `min-w-0` lets the value shrink below its content width. The label
    // beside it never shrinks, so without this a value that can't wrap on its
    // own (a badge next to a number) pushes itself out of the panel.
    <span className="min-w-0 text-right text-sm text-foreground">
      {item.copyable === false ? (
        item.value
      ) : (
        <CopyableValue
          value={item.value}
          copyValue={item.copyValue}
          copyOptions={item.copyOptions}
          label={item.label}
        />
      )}
    </span>
  );
}

export function KeyValueList({ items }: KeyValueListProps) {
  return (
    <div>
      {items.map((item, i) => {
        const divider = i < items.length - 1 ? "border-b border-border" : "";
        const subItems = item.subItems;

        if (subItems && subItems.length > 0) {
          return (
            <div key={item.label} className={divider}>
              <div className="pt-2.5 pb-1.5">
                <span className="text-sm font-bold text-content">
                  {item.label}
                </span>
              </div>
              <div className="mb-2 ml-1 flex flex-col border-l-2 border-border pl-4">
                {subItems.map((sub, j) => (
                  <div
                    key={sub.label}
                    className={`flex items-center justify-between gap-4 py-1.5 ${
                      j < subItems.length - 1 ? "border-b border-border/50" : ""
                    }`}
                  >
                    <span className="shrink-0 text-sm font-medium text-content">
                      {sub.label}
                    </span>
                    <ValueCell_ item={sub} />
                  </div>
                ))}
              </div>
            </div>
          );
        }

        return (
          <div
            key={item.label}
            className={`flex items-center justify-between gap-4 py-2.5 ${divider}`}
          >
            <span className="shrink-0 text-sm font-bold text-content">
              {item.label}
            </span>
            <ValueCell_ item={item} />
          </div>
        );
      })}
    </div>
  );
}
